import { ApiError, type ParityGate, type ParityHistory, type ParitySeries, type ReadinessArea, type ReadinessCheck, type ReadinessKey, type ReadinessRow } from "./api";
import type { SortColumn } from "./sort";

/** A share to one decimal, cut down: a share under a gate never shows as the gate. */
export const percentText = (share: number | null | undefined) =>
  share === null || share === undefined ? "–" : `${Math.floor(share * 1000 + 1e-9) / 10}%`;

const cyclesText = (count: number) => (count === 1 ? "1 cycle" : `${count} cycles`);

/** OQ-O19: the gate as one sentence. */
export const gateText = (gate: ParityGate) =>
  `The gate: ${cyclesText(gate.cycles)} in a row with ${Math.round(gate.share * 100)}% of the rows within ${Math.round(gate.tolerance * 100)}%, and no difference with no explanation.`;

export const streakText = (streak: number, cycles: number) => `${Math.min(streak, cycles)} of ${cyclesText(cycles)}`;

export interface TrendRow {
  key: string;
  label: string;
  series: ParitySeries;
}

/** The organization first, then each area. */
export const trendRows = (history: ParityHistory): TrendRow[] => [
  { key: "", label: "Organization", series: history.organization },
  ...history.areas.map((area) => ({ key: area.area, label: area.area, series: area })),
];

/** The share of each day, null on a day with no row or no rows to compare. */
export const shares = (series: ParitySeries): (number | null)[] =>
  series.total.map((total, i) => {
    const agree = series.agree[i];
    return total === null || agree === null || total === 0 ? null : agree / total;
  });

/** A line needs 2 days with a share: one dot tells nothing. */
export const showsTrend = (series: ParitySeries) => shares(series).filter((share) => share !== null).length >= 2;

/** The lines of a small chart: one polyline for each run of days with a share. Share 0.5 is the bottom. */
export function sparkLines(values: readonly (number | null)[], width: number, height: number, floor = 0.5): string[] {
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const y = (share: number) => {
    const clamped = Math.min(1, Math.max(floor, share));
    return Math.round((height - ((clamped - floor) / (1 - floor)) * height) * 10) / 10;
  };
  const lines: string[] = [];
  let current: string[] = [];
  values.forEach((value, i) => {
    if (value === null) {
      if (current.length) lines.push(current.join(" "));
      current = [];
      return;
    }
    current.push(`${Math.round(i * step * 10) / 10},${y(value)}`);
  });
  if (current.length) lines.push(current.join(" "));
  return lines;
}

export const gateY = (gate: number, height: number, floor = 0.5) =>
  Math.round((height - ((Math.min(1, Math.max(floor, gate)) - floor) / (1 - floor)) * height) * 10) / 10;

export const TREND_COLUMNS: SortColumn<TrendRow>[] = [
  { key: "area", label: "Area", value: (r) => (r.key === "" ? "" : r.label) },
  { key: "trend", label: "Trend", value: (r) => r.series.latest?.share ?? null },
  { key: "share", label: "Last night", value: (r) => r.series.latest?.share ?? null, first: "desc", num: true },
  { key: "unexplained", label: "No explanation", value: (r) => r.series.latest?.unexplained ?? null, first: "desc", num: true },
  { key: "streak", label: "Cycles met", value: (r) => r.series.streak, first: "desc", num: true },
  { key: "gate", label: "Gate", value: (r) => (r.series.meets ? "met" : "not"), order: ["not", "met"], num: true },
];

/** Design 26.11 F3, SO-4101: the name of each check, as the column header and the checkbox label. */
export const CHECK_TEXT: Record<ReadinessKey, string> = {
  parity: "Parity gate",
  import: "No import errors",
  accounts: "Accounts with clean data",
  drivers: "Drivers with a phone sync",
  truck: "A home truck",
  routes: "An accepted shadow week",
};

/** Rule U-5: the short column headers. The full name is in the title and in the phone card label. */
export const CHECK_SHORT: Record<ReadinessKey, string> = {
  parity: "Parity gate",
  import: "Import",
  accounts: "Data",
  drivers: "Drivers",
  truck: "Truck",
  routes: "Shadow week",
};

/** The rule of each check. Each starts with "Passes when", so that it does not read as the result. */
export const CHECK_HELP: Record<ReadinessKey, string> = {
  parity: "Passes when the gate of the parity history passes for this area.",
  import: "Passes when no open import issue with an error code is on an account of the area.",
  accounts: "Passes when each account has a front door, a container pin, containers and a service plan with a rate.",
  drivers: "Passes when each driver who drove the area in the last 8 weeks has a user, a role and a visit or a started route in the last 7 days.",
  truck: "Passes when a truck has its home in the area.",
  routes: "Passes when a person accepted the planner routes of a day with stops in the area.",
};

export type CheckState = "passes" | "accepted" | "fails";

export const checkState = (check: Pick<ReadinessCheck, "passed" | "accepted">): CheckState =>
  check.passed ? "passes" : check.accepted ? "accepted" : "fails";

/** WCAG 1.4.11: the state is a chip with an icon and a word, not only a colour or a checkbox. */
export const CHECK_STATE: Record<CheckState, { icon: string; text: string; chip: string }> = {
  passes: { icon: "✓", text: "Passes", chip: "ok" },
  accepted: { icon: "!", text: "Accepted", chip: "warn" },
  fails: { icon: "✕", text: "Fails", chip: "error" },
};

/** The chip text: an accepted check says so in words, not only with "!" (16-ux 16.6). */
export const checkChipText = (check: Pick<ReadinessCheck, "key" | "passed" | "accepted">): string =>
  checkState(check) === "accepted" ? `${CHECK_SHORT[check.key]} accepted` : CHECK_SHORT[check.key];

const plural = (count: number, one: string, many: string) => `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;

/** The number beside a checkbox: a share where a share makes sense, else a count. */
export function checkValueText(check: ReadinessCheck, cycles: number): string {
  if (check.key === "parity") return check.of === null ? "No history" : `${percentText(check.share)} now, ${streakText(check.streak ?? 0, cycles)}`;
  if (check.key === "import") return check.done ? plural(check.done, "error", "errors") : "No errors";
  if (check.key === "truck") return check.done ? plural(check.done, "truck", "trucks") : "No home truck";
  if (check.key === "routes") return check.done ? plural(check.done, "day", "days") : "No accepted day";
  if (check.of === 0 || check.of === null) return check.key === "drivers" ? "No driver yet" : "No accounts";
  return `${percentText(check.share)} (${check.done ?? 0} of ${check.of})`;
}

export const readyText = (area: ReadinessArea) =>
  area.ready ? "Ready" : `${area.checks.filter((check) => check.ok).length} of ${area.checks.length} checks`;

/** The checks with rows that fail. The others link to a page. */
export const HAS_ROWS: readonly ReadinessKey[] = ["import", "accounts", "drivers"];

export const MISSING_TEXT: Record<string, string> = {
  door: "front door",
  pin: "container pin",
  containers: "containers",
  plan: "service plan rate",
  user: "active user",
  role: "role",
  sync: "sync in 7 days",
};

export const missingText = (row: ReadinessRow) => (row.missing ?? []).map((key) => MISSING_TEXT[key] ?? key).join(", ");

export const ACCEPT_MAX = 500;

export const acceptProblem = (reason: string) =>
  !reason.trim() ? "Say why the check is accepted." : reason.length > ACCEPT_MAX ? `A reason has ${ACCEPT_MAX} characters at most. This one has ${reason.length}.` : null;

/** Plain words for a failed accept (rule U-17), never the server text. */
export function acceptFailure(e: unknown, online = typeof navigator === "undefined" || navigator.onLine): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (!status) return online ? "Not saved. Try again." : "You are offline. Connect, then try again.";
  if (status === 401) return "Your session ended. Sign in again, then try again.";
  if (status === 403) return "Your role cannot accept a readiness check.";
  if (status === 404) return "This area is not there now. Reload the page to see the areas.";
  if (status === 422) return `The accept was not saved. Check the reason (${ACCEPT_MAX} characters at most), then try again.`;
  if (status >= 500) return "The server did not answer. Try again in a few minutes.";
  return "The accept was not saved. Reload the page, then try again.";
}

export interface GateForm {
  cycles: string;
  share: string;
  tolerance: string;
}

export const gateForm = (gate: ParityGate): GateForm => ({
  cycles: String(gate.cycles),
  share: String(Math.round(gate.share * 1000) / 10),
  tolerance: String(Math.round(gate.tolerance * 1000) / 10),
});

/** The gate from the form, or the problem. The form has percents; the API has fractions. */
export function gateFromForm(form: GateForm): ParityGate | string {
  const cycles = Number(form.cycles);
  const share = Number(form.share);
  const tolerance = Number(form.tolerance);
  if (!Number.isInteger(cycles) || cycles < 1 || cycles > 12) return "Cycles is a whole number from 1 to 12.";
  if (!Number.isFinite(share) || share < 50 || share > 100) return "The share is from 50% to 100%.";
  if (!Number.isFinite(tolerance) || tolerance < 1 || tolerance > 50) return "The tolerance is from 1% to 50%.";
  return { cycles, share: Math.round(share * 10) / 1000, tolerance: Math.round(tolerance * 10) / 1000 };
}
